import { ReactElement } from 'react';
import { WarningIcon } from '../../../../assets/icons';
import { Button } from '../../../../core/components';
import { SUPPORT_PHONE_NUMBER } from '../../../../core/constants';
import { useIsDesktop, usePageTitle } from '../../../../core/hooks';
import { tokens } from '../../../../core/theme';
import { REQUEST_TYPE_CONFIG, RequestType } from '../../../../core/types';
import { FORM_MAX_WIDTH } from '../../constants';
import { useAutoFocusRef } from '../../hooks';

// Fixo nos dois breakpoints no Figma.
const CONTENT_GAP = 16;
const MOBILE_ICON_TO_TITLE_GAP = 4;
const DESKTOP_ICON_TO_TITLE_GAP = 16;
const MOBILE_ICON_SIZE = 32;
const DESKTOP_ICON_SIZE = 44;
// No mobile o conteúdo fica ancorado no topo (62px abaixo da logo) e o botão fixo no rodapé da tela.
const MOBILE_HEADER_TO_ICON_GAP = 62;

type ErrorStepProps = {
  type: RequestType;
  onRetry: () => void;
  isRetrying?: boolean;
};

export const ErrorStep = ({ type, onRetry, isRetrying = false }: ErrorStepProps): ReactElement => {
  const isDesktop = useIsDesktop();
  const iconSize = isDesktop ? DESKTOP_ICON_SIZE : MOBILE_ICON_SIZE;
  const headingRef = useAutoFocusRef<HTMLHeadingElement>();
  usePageTitle(`${REQUEST_TYPE_CONFIG[type].label} — Erro no envio`);

  return (
    <div style={styles.page}>
      <div
        style={{
          ...styles.form,
          gap: CONTENT_GAP,
          justifyContent: isDesktop ? 'center' : 'flex-start',
        }}
      >
        <div
          style={{
            ...styles.messageHeader,
            gap: isDesktop ? DESKTOP_ICON_TO_TITLE_GAP : MOBILE_ICON_TO_TITLE_GAP,
            marginTop: isDesktop ? 0 : MOBILE_HEADER_TO_ICON_GAP,
          }}
        >
          <WarningIcon width={iconSize} height={iconSize} aria-hidden="true" focusable="false" />
          <h1 ref={headingRef} tabIndex={-1} style={styles.title}>
            Não foi possível enviar sua solicitação
          </h1>
          <p style={styles.subtitle}>
            Tente novamente em alguns minutos.
            <br />
            Se precisar, ligue <strong>{SUPPORT_PHONE_NUMBER}</strong>
          </p>
        </div>
        <div
          style={{
            width: '100%',
            marginTop: isDesktop ? 0 : 'auto',
            marginBottom: isDesktop ? 0 : tokens.spacing.small,
          }}
        >
          <Button variant="primary" disabled={isRetrying} onClick={onRetry} expanded={!isDesktop}>
            {isRetrying ? 'Enviando...' : 'Tentar Novamente'}
          </Button>
        </div>
      </div>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  page: {
    display: 'flex',
    justifyContent: 'center',
    width: '100%',
    minHeight: '100%',
    padding: `${tokens.spacing.default} ${tokens.spacing.pageGutter}`,
    boxSizing: 'border-box',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    width: '100%',
    maxWidth: FORM_MAX_WIDTH,
    flex: 1,
    boxSizing: 'border-box',
  },
  messageHeader: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
  },
  title: {
    margin: tokens.spacing.none,
    fontSize: tokens.typography.fontSize.subtitle,
    fontWeight: tokens.typography.fontWeight.medium,
    color: tokens.colors.global.black,
    textAlign: 'center',
  },
  subtitle: {
    margin: tokens.spacing.none,
    fontSize: tokens.typography.fontSize.body,
    fontWeight: tokens.typography.fontWeight.regular,
    color: tokens.colors.brand.gray.DEFAULT,
    textAlign: 'center',
  },
};
